import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  DELETE_FINANCIAL_YEAR,
  LIST_FINANCIAL_YEARS_PAGEABLE,
  SAVE_FINANCIAL_YEAR,
} from '@modules/settings/settings.graphql';
import {
  ListFinancialYearsPageableResponse,
  SaveFinancialYearResponse,
  DeleteFinancialYearResponse,
} from '@modules/settings/types/financial-year';
import { FinancialYearActions } from './financial-year.actions';

@Injectable()
export class FinancialYearEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphqlService = inject(GraphQlService);
  private readonly swalService = inject(SwalService);

  loadAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialYearActions.loadAll),
      switchMap(({ pageableParam }) =>
        this.graphqlService
          .query<ListFinancialYearsPageableResponse>({
            query: LIST_FINANCIAL_YEARS_PAGEABLE,
            apolloNamespace: ApolloNamespace.settings,
            variables: pageableParam,
          })
          .pipe(
            map((response) =>
              FinancialYearActions.loadAllSuccess({
                data: response.data?.listFinancialYearsPageable?.content ?? [],
                totalElements: response.data?.listFinancialYearsPageable?.totalElements ?? 0,
              })
            ),
            catchError((error) => of(FinancialYearActions.loadAllFailure({ error: error.message })))
          )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialYearActions.create),
      mergeMap(({ input }) =>
        this.graphqlService
          .mutate<SaveFinancialYearResponse>({
            mutation: SAVE_FINANCIAL_YEAR,
            apolloNamespace: ApolloNamespace.settings,
            variables: { input },
          })
          .pipe(
            map((response) => {
              const res = response.data?.saveFinancialYear;
              if (res?.status && res.data) return FinancialYearActions.createSuccess({ entity: res.data });
              return FinancialYearActions.createFailure({ error: res?.message ?? 'Failed to save financial year' });
            }),
            catchError((error) => of(FinancialYearActions.createFailure({ error: error.message })))
          )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialYearActions.update),
      mergeMap(({ input }) =>
        this.graphqlService
          .mutate<SaveFinancialYearResponse>({
            mutation: SAVE_FINANCIAL_YEAR,
            apolloNamespace: ApolloNamespace.settings,
            variables: { input },
          })
          .pipe(
            map((response) => {
              const res = response.data?.saveFinancialYear;
              if (res?.status && res.data) return FinancialYearActions.updateSuccess({ entity: res.data });
              return FinancialYearActions.updateFailure({ error: res?.message ?? 'Failed to update financial year' });
            }),
            catchError((error) => of(FinancialYearActions.updateFailure({ error: error.message })))
          )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialYearActions.delete),
      mergeMap(({ uuid }) =>
        this.graphqlService
          .mutate<DeleteFinancialYearResponse>({
            mutation: DELETE_FINANCIAL_YEAR,
            apolloNamespace: ApolloNamespace.settings,
            variables: { uuid },
          })
          .pipe(
            map((response) => {
              const res = response.data?.deleteFinancialYear;
              if (res?.status) return FinancialYearActions.deleteSuccess({ uuid });
              return FinancialYearActions.deleteFailure({ error: res?.message ?? 'Failed to delete financial year' });
            }),
            catchError((error) => of(FinancialYearActions.deleteFailure({ error: error.message })))
          )
      )
    )
  );

  notifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          FinancialYearActions.createSuccess,
          FinancialYearActions.updateSuccess,
          FinancialYearActions.deleteSuccess,
          FinancialYearActions.createFailure,
          FinancialYearActions.updateFailure,
          FinancialYearActions.deleteFailure
        ),
        tap((action) => {
          if (action.type === FinancialYearActions.createSuccess.type) {
            this.swalService.successToast('Financial year saved successfully');
          } else if (action.type === FinancialYearActions.updateSuccess.type) {
            this.swalService.successToast('Financial year updated successfully');
          } else if (action.type === FinancialYearActions.deleteSuccess.type) {
            this.swalService.successToast('Financial year deleted successfully');
          } else if (
            action.type === FinancialYearActions.createFailure.type ||
            action.type === FinancialYearActions.updateFailure.type ||
            action.type === FinancialYearActions.deleteFailure.type
          ) {
            const anyAction: any = action;
            this.swalService.errorToast(anyAction.error?.toString?.() ?? 'Operation failed');
          }
        })
      ),
    { dispatch: false }
  );
}
